import React from 'react';
import { WrappedComponentProps } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { withRouter } from 'react-router';
import { getTranslation } from '../../resources/utils';
import { changeNavigationCurrentPage } from '../../store/actions/navigation';
import { ApplicationState, StoreDispatch } from '../../store/types';
import { RouterProps } from '../../types/generalTypes';
import { ShopNavigationMenu, ShopNavigationSubMenu } from '../../types/settings';
import Container from '../common/container';
import LoadingCircle from '../common/loadingCircle';

type ReduxProps = ConnectedProps<typeof connector>;

interface IProps {
}

type Props = IProps & RouterProps & WrappedComponentProps & ReduxProps;

interface State { }

class NavigationPage extends React.Component<Props, State> {
    iframe: HTMLIFrameElement | null = null;

    iframeObserver?: ResizeObserver;

    componentDidMount() {
        const {
            match, menus, runChangeNavigationCurrentPage, history,
        } = this.props;
        const {
            lang, school, menu, submenu,
        } = match.params;
        window.scrollTo(0, 0);
        // The Joomla page is cross-origin and sized to its content, so it never scrolls itself:
        // scroll-driven modules (e.g. the medals timeline) need this page's viewport (ess-viewport protocol)
        window.addEventListener('scroll', this.sendViewport, { passive: true });
        window.addEventListener('resize', this.sendViewport);
        window.addEventListener('message', this.handleIframeMessage);

        if (menus) {
            const navigationMenu = menus.find((m: ShopNavigationMenu) => m.title.replace(' ', '-') === menu);

            if (navigationMenu) {
                if (navigationMenu.submenus.length === 0 && !submenu) {
                    runChangeNavigationCurrentPage(navigationMenu);
                    return;
                }
                const navigationSubMenu = navigationMenu.submenus.find((s: ShopNavigationSubMenu) => s.title.replace(' ', '-') === submenu);
                if (navigationSubMenu) {
                    runChangeNavigationCurrentPage(navigationSubMenu);
                    return;
                }
            }
            history.replace(`/${lang}/${school}`);
        }
    }

    componentDidUpdate(prevProps: Props) {
        const {
            currentPage, menus, runChangeNavigationCurrentPage, history, match,
        } = this.props;
        // if the user load the page or change the current page
        if ((currentPage === undefined && menus !== undefined)
            || (prevProps.currentPage !== currentPage)) {
            const {
                lang, school, menu, submenu,
            } = match.params;

            const navigationMenu = menus ? menus.find((m: ShopNavigationMenu) => m.title.replace(' ', '-') === menu) : undefined;

            if (navigationMenu) {
                if (navigationMenu.submenus.length === 0 && !submenu) {
                    runChangeNavigationCurrentPage(navigationMenu);
                    return;
                }
                const navigationSubMenu = navigationMenu.submenus.find((s: ShopNavigationSubMenu) => s.title.replace(' ', '-') === submenu);
                if (navigationSubMenu) {
                    runChangeNavigationCurrentPage(navigationSubMenu);
                    return;
                }
            }
            history.replace(`/${lang}/${school}`);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.sendViewport);
        window.removeEventListener('resize', this.sendViewport);
        window.removeEventListener('message', this.handleIframeMessage);
    }

    handleIframeMessage = (event: MessageEvent) => {
        const page = this.getPage();
        if (!this.iframe || event.source !== this.iframe.contentWindow || !page || event.origin !== new URL(page).origin) return;
        if (event.data?.type === 'ess-ready') this.sendViewport();
        else if (event.data?.type === 'ess-scroll-by' && Number.isFinite(event.data.dy)) {
            window.scrollBy({ top: event.data.dy, behavior: 'smooth' });
        }
    };

    setIframe = (iframe: HTMLIFrameElement | null) => {
        this.iframeObserver?.disconnect();
        this.iframe = iframe;
        if (iframe) {
            // Resend once Joomla has resized the iframe, otherwise the module sees the short page as scrolled to the end
            this.iframeObserver = new ResizeObserver(this.sendViewport);
            this.iframeObserver.observe(iframe);
        }
    };

    sendViewport = () => {
        const page = this.getPage();
        if (!this.iframe?.contentWindow || !page) return;
        this.iframe.contentWindow.postMessage({
            type: 'ess-viewport',
            top: this.iframe.getBoundingClientRect().top,
            height: window.innerHeight,
            end: window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4,
        }, page);
    };

    getTitle = () => {
        const { currentPage, match } = this.props;
        if (currentPage && !currentPage.translations) {
            const translation = getTranslation(currentPage.translations, match.params.lang);
            return translation ? translation.title : '';
        }
        return '';
    };

    onLoad = () => {
        const iframe: any = document.getElementById('navigation-page-iframe');
        iframe.contentWindow.postMessage('navigation', this.getPage());
        this.sendViewport();
    };

    getPage = () => {
        const { currentPage, match } = this.props;
        if (currentPage) {
            const translation = getTranslation(currentPage.translations, match.params.lang).page;
            return translation;
        }
        return undefined;
    };

    render() {
        const { contentHeight } = this.props;
        const page = this.getPage();
        const headerBase: any = document.getElementsByClassName('__header-base')[0];
        if (headerBase && headerBase.style) headerBase.style.display = 'none';
        return (
            <Container
                headerBase={this.getTitle()}
                noPadding
            >
                <div>
                    {
                        page
                            ? (
                                <iframe
                                    ref={this.setIframe}
                                    title="Navigation iFrame"
                                    id="navigation-page-iframe"
                                    className="__help-iframe"
                                    style={{ height: contentHeight || '100%', overflow: 'hidden' }}
                                    src={page}
                                    onLoad={this.onLoad}
                                />
                            )
                            : <LoadingCircle />
                    }
                </div>
            </Container>
        );
    }
}

const mapStateToProps = (store: ApplicationState) => ({
    menus: store.navigation.menus,
    currentPage: store.navigation.currentPage,
    contentHeight: store.navigation.contentHeight,
});
const mapDispatchToProps = (dispatch: StoreDispatch) => ({
    runChangeNavigationCurrentPage: (page: ShopNavigationMenu | ShopNavigationSubMenu) => dispatch(changeNavigationCurrentPage(page)),
});
const connector = connect(mapStateToProps, mapDispatchToProps);

export default withRouter(connector(NavigationPage));
